import { NavLink, Outlet, useLocation } from "react-router-dom";
import { LayoutGrid, CalendarDays, Package, Wine, TrendingUp, LogOut, BedDouble, Building2, UserPlus, ChefHat } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { LOGO_URL, BRAND_NAME } from "@/lib/api";
import { toast } from "sonner";

const nav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutGrid, testid: "nav-dashboard" },
  { to: "/reservations", label: "Reservations", icon: CalendarDays, testid: "nav-reservations" },
  { to: "/pos", label: "Bar POS", icon: Wine, testid: "nav-pos" },
  { to: "/revenue", label: "Revenue", icon: TrendingUp, testid: "nav-revenue" },
  { to: "/products", label: "Products", icon: Package, testid: "nav-products" },
  { to: "/rooms", label: "Rooms", icon: BedDouble, testid: "nav-rooms" },
  { to: "/partners", label: "Partners", icon: Building2, testid: "nav-partners" },
  { to: "/leads", label: "Leads", icon: UserPlus, testid: "nav-leads" },
  { to: "/kitchen", label: "Kitchen", icon: ChefHat, testid: "nav-kitchen" },
];

export default function Layout() {
  const loc = useLocation();
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    toast.success("Signed out");
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <aside className="fixed left-0 top-0 h-screen w-52 border-r border-slate-200 bg-white z-40 flex flex-col">
        <div className="h-20 flex items-center gap-2.5 px-4 border-b border-slate-200">
          <div className="h-11 w-11 rounded-md bg-slate-950 flex items-center justify-center shrink-0 p-1">
            <img src={LOGO_URL} alt={BRAND_NAME} className="h-full w-full object-contain" />
          </div>
          <div className="min-w-0">
            <div className="font-heading font-bold text-slate-900 leading-tight truncate">Ondina</div>
            <div className="text-[11px] text-slate-500 mt-0.5 truncate">Guesthouse PMS</div>
          </div>
        </div>
        <nav className="flex-1 p-2.5 space-y-1">
          {nav.map((n) => {
            const active = loc.pathname.startsWith(n.to);
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={n.testid}
                className={cn(
                  "flex items-center gap-2.5 px-2.5 py-2 rounded-md text-sm font-medium transition-colors",
                  active
                    ? "bg-blue-50 text-blue-700"
                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                )}
              >
                <Icon size={17} strokeWidth={1.75} />
                <span>{n.label}</span>
              </NavLink>
            );
          })}
        </nav>
        <div className="p-2.5 border-t border-slate-200 space-y-2">
          {user?.email && (
            <div className="px-2 py-1.5">
              <div className="text-[10px] uppercase tracking-wider text-slate-400">Signed in</div>
              <div className="text-xs text-slate-700 truncate" data-testid="layout-user-email">{user.email}</div>
            </div>
          )}
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-2.5 py-2 rounded-md text-sm text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors"
            data-testid="layout-logout-btn"
          >
            <LogOut size={15} strokeWidth={1.75} /> Sign out
          </button>
        </div>
      </aside>
      <main className="ml-52 min-h-screen">
        <Outlet />
      </main>
    </div>
  );
}
